import {
    ActionIcon,
    Group,
    Pill,
    Text,
    Tooltip,
    UnstyledButton,
    useMantineTheme,
    Popover,
} from '@mantine/core';
import { IconMoodPlus } from '@tabler/icons-react';
import { useState, useMemo, useEffect, useCallback } from 'react';
import data, { Emoji } from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import i18nKo from '@emoji-mart/data/i18n/ko.json';
import i18nVi from '@emoji-mart/data/i18n/vi.json';
import { useTranslation } from 'react-i18next';
import { useEmojiReactions } from '../hooks/useEmojiReactionQueries';
import {
    useFeedbackReactionNotificationWatcher,
    useGroupedEmojiReactions,
} from '../hooks/useEmojiReaction';
import { EmojiReactionWithInfo } from '@/common/types/commonTypes';
import { useAuthStore } from '@/stores/authStore';
import { EntityType } from '../services/emojiReactionService';

// 상수로 스타일 객체들을 컴포넌트 외부에 선언
const PILL_LABEL_STYLES = {
    label: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
} as const;

// 기본 인터페이스
interface BaseEmojiReactionGroupProps {
    entityType: EntityType;
    entityId: string;
    editable: boolean;
    emojiReactions: EmojiReactionWithInfo[];
}

// 피드백용 인터페이스
interface FeedbackEmojiReactionGroupProps extends BaseEmojiReactionGroupProps {
    entityType: 'feedback';
    notificationConfig: {
        notificationReceiverId: string;
        feedbackReceiverId: string;
        feedbackContent: string;
        workspaceId: string;
    };
}

// 데일리스크럼용 인터페이스
interface DailyScrumEmojiReactionGroupProps
    extends BaseEmojiReactionGroupProps {
    entityType: 'daily_scrum_item';
    notificationConfig?: never; // 명시적으로 없음을 표시
}

// 유니온 타입
type EmojiReactionGroupProps =
    | FeedbackEmojiReactionGroupProps
    | DailyScrumEmojiReactionGroupProps;

export const EmojiReactionGroup = ({
    entityType,
    entityId,
    editable,
    emojiReactions,
    notificationConfig,
}: EmojiReactionGroupProps) => {
    const theme = useMantineTheme();
    const { t, i18n } = useTranslation(['common']);
    const { user } = useAuthStore.getState();

    const [opened, setOpened] = useState(false);
    const [hasUserInteracted, setHasUserInteracted] = useState(false);

    const { toggleEmojiReaction } = useEmojiReactions(entityType, entityId);

    // 디바운스 타이머로 알림 처리 (피드백일 때만)
    const { startNotificationTimer, processImmediately } =
        useFeedbackReactionNotificationWatcher(
            entityType === 'feedback' ? entityId : '',
            entityType === 'feedback' ? notificationConfig : undefined
        );

    const groupedEmojiReactions = useGroupedEmojiReactions(emojiReactions);

    // 이모지 필 스타일을 useMemo로 캐싱
    const pillStyles = useMemo(
        () => ({
            selected: {
                backgroundColor: theme.colors.blue[0],
                border: `1px solid ${theme.colors.blue[5]}`,
            },
            unselected: {
                backgroundColor: theme.colors.gray[0],
                border: '1px solid transparent',
            },
        }),
        [theme]
    );

    // 컴포넌트 언마운트 시 피드백 리액션 알림 즉시 처리해 누락 방지
    useEffect(() => {
        return () => {
            if (
                entityType === 'feedback' &&
                hasUserInteracted &&
                processImmediately
            ) {
                void processImmediately();
            }
        };
    }, [entityType, hasUserInteracted, processImmediately]);

    // 이모지 토글 및 알림 처리
    const handleEmojiClick = useCallback(
        async (emojiCode: string, isSelected: boolean) => {
            if (!editable) return;

            // 사용자 상호작용 추적
            setHasUserInteracted(true);

            // 리액션 토글
            const updatedReactions = await toggleEmojiReaction(
                emojiCode,
                isSelected
            );

            // 피드백 리액션 알림 처리
            if (entityType === 'feedback') {
                startNotificationTimer?.(updatedReactions);
            }
        },
        [editable, toggleEmojiReaction, entityType, startNotificationTimer]
    );

    // 이모지 피커 open/close 핸들러
    const handleToggleOpen = useCallback(() => {
        setOpened(o => !o);
    }, []);

    // 모달 위에 띄웠을 때도 이모지 픽커 휠 이벤트 감지 가능하게
    const handleWheelEvent = useCallback((e: React.WheelEvent) => {
        e.stopPropagation();
    }, []);

    // 이모지 픽커 언어 파일 설정
    const emojiMartI18n = useMemo(() => {
        switch (i18n.language) {
            case 'ko-KR':
                return i18nKo;
            case 'vi-VN':
                return i18nVi;
            default:
                return undefined;
        }
    }, [i18n.language]);

    // 이모지 픽커 높이 조정
    useEffect(() => {
        const maxHeight = '40vh';

        // 스타일 요소 생성
        const styleElement = document.createElement('style');
        styleElement.innerHTML = `
            em-emoji-picker {
                height: ${maxHeight} !important;
                --em-emoji-picker-height: ${maxHeight} !important; 
            }
            em-emoji-picker #root {
                max-height: ${maxHeight} !important;
            }
        `;

        // 문서에 스타일 요소 추가
        document.head.appendChild(styleElement);

        // 컴포넌트 언마운트 시 스타일 요소 제거
        return () => {
            document.head.removeChild(styleElement);
        };
    }, []);

    return (
        <>
            <Group gap={8} mb="md">
                {/* 이모지 리액션 버튼과 픽커 */}
                {editable && (
                    <Popover
                        opened={opened}
                        onChange={setOpened}
                        position="bottom-start"
                        width="auto"
                        closeOnClickOutside
                        withinPortal
                    >
                        <Popover.Target>
                            <Tooltip label={t('emoji_reaction.add')} withArrow>
                                <ActionIcon
                                    size="md"
                                    radius="md"
                                    variant="subtle"
                                    color="gray"
                                    onClick={handleToggleOpen}
                                >
                                    <IconMoodPlus size={16} />
                                </ActionIcon>
                            </Tooltip>
                        </Popover.Target>

                        <Popover.Dropdown p={0} bg="transparent">
                            <div onWheel={handleWheelEvent}>
                                <Picker
                                    height={100}
                                    data={data}
                                    onEmojiSelect={(emoji: Emoji) => {
                                        void (async () => {
                                            await handleEmojiClick(
                                                // @ts-expect-error description: Emoji 타입 정의 파일에 native 프로퍼티가 없으나, 실제 데이터에서는 존재함
                                                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                                                emoji.native,
                                                emojiReactions.some(
                                                    e =>
                                                        e.user?.id ===
                                                            user?.id &&
                                                        e.emoji_code ===
                                                            // @ts-expect-error description: Emoji 타입 정의 파일에 native 프로퍼티가 없으나, 실제 데이터에서는 존재함
                                                            emoji.native
                                                )
                                            );
                                        })();
                                    }}
                                    maxFrequentRows={1}
                                    previewPosition="none"
                                    perLine={10}
                                    i18n={emojiMartI18n}
                                />
                            </div>
                        </Popover.Dropdown>
                    </Popover>
                )}

                {/* 이모지 리액션 목록 */}
                {groupedEmojiReactions.length > 0 && (
                    <Group gap={4}>
                        {groupedEmojiReactions.map(emoji => {
                            const isSelected = emojiReactions.some(
                                e =>
                                    e.user?.id === user?.id &&
                                    e.emoji_code === emoji.emoji_code
                            );

                            const tooltipLabel = emoji.users
                                .map(user => user.name)
                                .join(', ');

                            return (
                                <Tooltip
                                    key={emoji.emoji_code}
                                    label={tooltipLabel}
                                    withArrow
                                >
                                    <UnstyledButton
                                        style={{
                                            cursor: editable
                                                ? 'pointer'
                                                : 'default',
                                        }}
                                        onClick={() => {
                                            void handleEmojiClick(
                                                emoji.emoji_code,
                                                isSelected
                                            );
                                        }}
                                    >
                                        <Pill
                                            styles={PILL_LABEL_STYLES}
                                            style={
                                                isSelected
                                                    ? pillStyles.selected
                                                    : pillStyles.unselected
                                            }
                                        >
                                            <Text span>
                                                <Text size="xs" span>
                                                    {emoji.emoji_code}
                                                </Text>
                                                {emoji.users.length > 0 && (
                                                    <Text size="xs" span>
                                                        {' '}
                                                        {emoji.users.length}
                                                    </Text>
                                                )}
                                            </Text>
                                        </Pill>
                                    </UnstyledButton>
                                </Tooltip>
                            );
                        })}
                    </Group>
                )}
            </Group>
        </>
    );
};
